import { useCallback, useState } from 'react';
import { ButtonVariant } from 'src/components/atoms/Button';
import ToolTip, { ToolTipDirection } from 'src/components/atoms/ToolTip/ToolTip';
import { TermsCheck } from 'src/components/DUP/molecules/WizardFormApplicant';
import ApplicationInfo from 'src/components/DUP/molecules/WizardSubmit/ApplicationInfo';
import { useSubmissionValidation } from 'src/components/DUP/molecules/WizardSubmit/useSubmissionValidation';
import { useApplication } from 'src/context/ApplicationContext';
import { useStepTranslations } from 'src/hooks/useClientTranslations';
import { useRequest } from 'src/hooks/useResource';
import { useSnackbarProvider } from 'src/hooks/useSnackbarProvider';
import { StyledButton as Button } from 'src/pages/DUPPage/styles';
import { generateButtonId } from 'src/utils/telemetry';
import { ActionContainer, Container } from './styles';

const WizardSubmit = () => {
  const {
    proofs,
    steps: { onFinished, goBack }
  } = useApplication();
  const { tStep } = useStepTranslations();
  const { makeRequest } = useRequest();

  const { showSnackbar, SnackTypes, VariantType } = useSnackbarProvider();
  const { setBankLinkingReportId, isMovedTerms } = useApplication();

  const [isTOSChecked, setIsTOSChecked] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleError = useCallback(() => {
    setHasSubmitted(false);
    setShowLoading(false);
    showSnackbar(VariantType.error, tStep('errors.submission.title'), SnackTypes.none);
  }, [showSnackbar, tStep, VariantType.error, SnackTypes.none]);

  const { isSubmittable, disabledText } = useSubmissionValidation({
    proofs,
    hasSubmitted,
    isTOSChecked: isMovedTerms ? true : isTOSChecked
  });

  const onSubmit = async () => {
    if (isSubmittable) {
      setHasSubmitted(true);
      setShowLoading(true);
      const res = await makeRequest<{ applicantId: string; bankLinkingReportId?: string }>({
        path: '/applicant/submit',
        method: 'POST',
        payload: {
          proofs: proofs.map((proof) => ({ sourceName: proof.fileName || '' }))
        }
      });

      if (!('applicantId' in res)) {
        handleError();
      } else {
        res.bankLinkingReportId && setBankLinkingReportId(res.bankLinkingReportId);
        setShowLoading(false);
        onFinished();
      }
    }
  };

  return (
    <Container>
      <ApplicationInfo />
      {!isMovedTerms && <TermsCheck isChecked={isTOSChecked} onChange={setIsTOSChecked} />}
      <ActionContainer justify="space-between">
        <Button
          name="back"
          onClick={() => {
            goBack();
          }}
          variant={ButtonVariant.outline}
          telemetryId={generateButtonId('back')}
        >
          {tStep('backButton')}
        </Button>
        <ToolTip direction={ToolTipDirection.top_left} content={disabledText}>
          <Button
            name="submit"
            onClick={onSubmit}
            isLoading={showLoading}
            isDisabled={!isSubmittable}
            variant={ButtonVariant.contained}
            telemetryId={generateButtonId('submit')}
          >
            {tStep('submitButton')}
          </Button>
        </ToolTip>
      </ActionContainer>
    </Container>
  );
};

export default WizardSubmit;
